/**
 * /request-invite
 *
 * Public page. Anyone can request access to NachoFiles.
 * Collects email, desired username, display name, phone, and an optional note.
 * On submit: shows a confirmation, no account is created yet.
 * An admin reviews the request and approves/denies it.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client.js';
import { submitInviteRequest } from '../api/invite-requests.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import './RequestInvitePage.css';
import PhoneInput from '../components/PhoneInput.jsx';


const USERNAME_RE = /^[a-z0-9_-]{3,32}$/;
const PHONE_RE = /^\+[1-9]\d{7,14}$/;


export default function RequestInvitePage() {
  usePageTitle('Request an invite');

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [note, setNote] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const usernameNormalized = username.trim().toLowerCase();
  const usernameValid = USERNAME_RE.test(usernameNormalized);
  const emailValid = email.trim().includes('@');
  const displayNameValid = displayName.trim().length >= 1 && displayName.trim().length <= 64;
  const phoneValid = PHONE_RE.test(phoneNumber.trim().replace(/[\s\-().]/g, ''));
  const canSubmit = !submitting && emailValid && usernameValid && displayNameValid && phoneValid;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');

    try {
      await submitInviteRequest({
        email: email.trim(),
        username: usernameNormalized,
        display_name: displayName.trim(),
        phone_number: phoneNumber.trim().replace(/[\s\-().]/g, ''),
        note: note.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'something went wrong — try again');
    } finally {
      setSubmitting(false);
    }
  }

  // ── success state ──

  if (submitted) {
    return (
      <div className="request-page">
        <main className="request-stage">
          <section className="request-card">
            <div className="request-card-head">
              <span className="request-sticker success">✓ request sent</span>
              <h1 className="request-title">You're on the list</h1>
              <p className="request-sub">
                an admin will review your request and you'll get an email
                when there's a decision. keep calm and check your spam folder.
              </p>
            </div>
            <Link to="/login" className="request-back-link">
              ← back to sign in
            </Link>
          </section>
        </main>
        <div className="request-tagline">
          <strong>NachoFiles</strong> — the reason the chicken crossed the road
        </div>
      </div>
    );
  }

  // ── form ──

  return (
    <div className="request-page">
      <main className="request-stage">
        <section className="request-card">
          <div className="request-card-head">
            <span className="request-sticker">📬 request access</span>
            <h1 className="request-title">Want in?</h1>
            <p className="request-sub">
              fill this out and an admin will take a look.
              if approved, you'll get an email with your invite link.
              keep calm and check your spam folder.
            </p>
          </div>

          {error && (
            <div className="request-error" role="alert">
              <span aria-hidden="true">⚠</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="request-field">
              <label className="request-label" htmlFor="rq-email">email</label>
              <input
                id="rq-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
              <div className="request-help-text">
                we'll send your invite link here if approved
              </div>
            </div>

            <div className="request-field">
              <label className="request-label" htmlFor="rq-username">username</label>
              <input
                id="rq-username"
                type="text"
                autoComplete="username"
                placeholder="something-short"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <div className="request-help-text">
                3–32 characters, letters/digits/dash/underscore
              </div>
            </div>

            <div className="request-field">
              <label className="request-label" htmlFor="rq-display">display name</label>
              <input
                id="rq-display"
                type="text"
                autoComplete="name"
                placeholder="what should we call you?"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="request-field">
              <label className="request-label" htmlFor="rq-phone">phone number</label>
              <PhoneInput
                id="rq-phone"
                value={phoneNumber}
                onChange={setPhoneNumber}
              />
            </div>

            <div className="request-field">
              <label className="request-label" htmlFor="rq-note">
                why do you want to join?
                <span className="request-optional">optional</span>
              </label>
              <textarea
                id="rq-note"
                placeholder="say hi, explain the connection, or leave it blank"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={500}
              />
            </div>

            <button
              type="submit"
              className="request-submit"
              disabled={!canSubmit}
            >
              {submitting ? 'sending…' : 'submit request  →'}
            </button>
          </form>

          <div className="request-divider" />

          <Link to="/login" className="request-back-link">
            already have an account? <strong>sign in →</strong>
          </Link>
        </section>
      </main>

      <div className="request-tagline">
        <strong>NachoFiles</strong> — the reason the chicken crossed the road
      </div>
    </div>
  );
}
